import React from 'react';
import { Button, Theme, makeStyles } from '@material-ui/core';
import { AlertModal } from '../Modals';
import { GraySmall, NonHoverBorder, red, redDark } from '../../theme/colors';
import { typography15MediumStyle } from '../Text';
import { AlertCircleIcon } from '../Icons';
import BaseTypography from '../Text/BaseTypography';
import {
  APP_SETUP_PAGE,
  DISABLE_APP_DESCRIPTION,
  ModuleSettingsItem,
  ModuleType,
  REMOVE_APP_DESCRIPTION,
} from '../../constants';
import {
  DefaultModule,
  defaultModules,
} from '../../hooks/usePortalModulesState';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from '../../store/settings/actions';
import { FlagsContext, RouteContext } from '../../context';
import { useEditingModule } from '../Settings/ModuleManagementPage/SetupModule/useEditingModule';
import { useAppDispatch, useAppSelector } from '../../hooks/useStore';
import history from '../../history';

const useStyles = makeStyles((theme: Theme) => ({
  deleteButton: {
    marginTop: theme.spacing(3),
    backgroundColor: red,
    borderColor: red,
    '&:hover': {
      backgroundColor: red,
    },
  },
  disabledButton: {
    marginTop: theme.spacing(3),
    backgroundColor: 'transparent',
    borderColor: NonHoverBorder,
    color: red,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  embedsContent: {
    color: redDark,
    ...typography15MediumStyle,
  },
  actionModal: {
    '& .MuiPaper-root': {
      width: 544,
    },
  },
  removeAppContent: {
    color: GraySmall,
    paddingTop: theme.spacing(0.5),
  },
}));

type AlertModalDataType = {
  modalBody: React.ReactElement;
  modalSuccessActionLabel: string;
  modalTitle: string;
  moduleType?: ModuleType;
  moduleId?: string;
};

/**
 * @description - Responsible for rendering the remove app button and the modal.
 * For core apps (Forms, Helpdesk, Billing etc), we only disable the app.
 * For other apps, we delete the app.
 * Additionally, we do not render this component when we're in add mode.
 */
export const RemoveApp = () => {
  const { EnableAppSetupImprovements } = React.useContext(FlagsContext);
  const { query } = React.useContext(RouteContext);
  const appModule = useEditingModule();
  const [
    deletingModuleData,
    setDeletingModuleData,
  ] = React.useState<AlertModalDataType | null>(null);
  const [isDeletingModule, setIsDeletingModule] = React.useState(false);
  const isCoreApp = appModule?.type === 'module';
  const isAddMode = 'preset' in query; // Represents if we are in add mode or edit mode
  const extensionsSettings = useAppSelector(
    state => state.settings.extensionsSettings,
  );
  const moduleSettings =
    useAppSelector(state => state.settings.moduleSettings) || [];
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const handleDeleteModuleActionClicked = () => {
    if (appModule?.type === 'module') {
      setDeletingModuleData({
        moduleType: appModule.type,
        moduleId: appModule.id,
        modalTitle: 'Are you sure you want to disable this module?',
        modalBody: (
          <BaseTypography fontType="15Regular">
            When you disable a module it will no longer be accessible by your
            team or clients. If you enable it again in the future all data from
            before will be restored.
          </BaseTypography>
        ),
        modalSuccessActionLabel: 'Disable',
      });
    } else {
      const extensionModuleEmbedCount =
        (extensionsSettings && extensionsSettings[appModule!.id].embedCount) ||
        0;

      // when extension has embeds, show the embed count text,
      // otherwise do not.
      const stagedModalBody =
        extensionModuleEmbedCount > 0 ? (
          <div>
            <BaseTypography fontType="15Regular">
              The connection data along with &nbsp;
              <span className={classes.embedsContent}>
                {extensionModuleEmbedCount}&nbsp;
                {extensionModuleEmbedCount > 1 ? 'embeds' : 'embed'}
              </span>
              &nbsp; will be deleted.
            </BaseTypography>
          </div>
        ) : (
          <BaseTypography>The connection data will be deleted.</BaseTypography>
        );
      setDeletingModuleData({
        moduleType: appModule!.type,
        moduleId: appModule!.id,
        modalTitle: 'Are you sure you want to delete this app?',
        modalBody: stagedModalBody,
        modalSuccessActionLabel: 'Delete',
      });
    }
  };

  const disableApp = async (updatedModuleId: string) => {
    // when moduleSettings is empty then use defaultModules to get updated modules
    // otherwise use moduleSettings to get updated modules
    const isModuleSettingsEmpty = moduleSettings.length === 0;
    const modules = isModuleSettingsEmpty ? defaultModules : moduleSettings;

    const updatedModules = modules.map(moduleItem => {
      // when updating using a default module we only want to keep the relevant fields
      const moduleSettingItem = isModuleSettingsEmpty
        ? {
            id: moduleItem.id,
            label: (moduleItem as DefaultModule).title,
            icon: (moduleItem as DefaultModule).iconName,
            type: 'module' as ModuleType,
            disabled: moduleItem.id === updatedModuleId,
          }
        : (moduleItem as ModuleSettingsItem);
      if (moduleItem.id === updatedModuleId) {
        return {
          ...moduleSettingItem,
          disabled: true,
        };
      }
      return moduleSettingItem;
    });

    await dispatch(updateModuleSettings(updatedModules));
  };

  const deleteApp = async (updatedModuleId: string) => {
    const updatedModules = moduleSettings.filter(
      moduleItem => moduleItem.id !== updatedModuleId,
    );
    const nonDeletedExtensionsConfigs = Object.entries(extensionsSettings || {})
      .filter(([extensionId]) => extensionId !== updatedModuleId)
      .reduce(
        (prev, [extensionId, extensionConfig]) => ({
          ...prev,
          [extensionId]: extensionConfig,
        }),
        {},
      );
    await dispatch(updateModuleSettings(updatedModules));
    await dispatch(updateExtensionSettingsAction(nonDeletedExtensionsConfigs));
  };

  const handleDeleteModuleConfirm = async () => {
    if (!deletingModuleData) return;
    setIsDeletingModule(true);
    const { moduleType, moduleId } = deletingModuleData;
    // if it is a core module we need to set it as disabled only
    if (moduleType === 'module') {
      await disableApp(moduleId as string);
    } else {
      // for extension modules we need to delete the module
      await deleteApp(moduleId as string);
    }

    // close deleting confirmation modal by resetting the deleting module data state.
    setDeletingModuleData(null);
    setIsDeletingModule(false);
    history.push(APP_SETUP_PAGE.path);
  };

  // We don't want to show the remove app button in add mode
  if (!EnableAppSetupImprovements || isAddMode) return null;

  return (
    <div>
      {deletingModuleData && (
        <AlertModal
          className={classes.actionModal}
          open={Boolean(deletingModuleData)}
          title={deletingModuleData?.modalTitle}
          successButtonLabel={deletingModuleData?.modalSuccessActionLabel}
          onSuccess={handleDeleteModuleConfirm}
          onClose={() => setDeletingModuleData(null)}
          cancelButtonLabel="Cancel"
          Icon={AlertCircleIcon}
          noCancelButton={false}
          isDeleteModal
          isLoading={isDeletingModule}
        >
          {deletingModuleData?.modalBody}
        </AlertModal>
      )}
      <div>
        <BaseTypography fontType="18Medium">
          {isCoreApp ? 'Disable app' : 'Remove app  '}
        </BaseTypography>
        <BaseTypography
          fontType="13Regular"
          className={classes.removeAppContent}
        >
          {isCoreApp ? DISABLE_APP_DESCRIPTION : REMOVE_APP_DESCRIPTION}
        </BaseTypography>
        <Button
          onClick={handleDeleteModuleActionClicked}
          variant="contained"
          color="primary"
          className={isCoreApp ? classes.disabledButton : classes.deleteButton}
        >
          {isCoreApp ? 'Disable' : 'Delete'}
        </Button>
      </div>
    </div>
  );
};
